import { loadPdfJs } from "./pdf_libs"

/**
 * PdfViewer LiveView hook
 *
 * Renders PDF pages to canvas elements using PDF.js.
 * Communicates page count and current page back to LiveView.
 *
 * Data attributes:
 *   data-url              - URL to fetch the PDF from
 *   data-scale            - Render scale (default: 1.5)
 *   data-field-overlays   - Optional JSON array of field definitions to show as
 *                           positioned indicators on the PDF. Each field needs:
 *                           {page, x, y, w, h, type, label}
 *
 * Pushes events:
 *   pdf_loaded   - {page_count} when PDF is ready
 *   pdf_error    - {message} on load failure
 *
 * Handles events:
 *   go_to_page   - {page} navigate to a specific page
 *   set_scale    - {scale} change zoom level
 *
 * Field sync:
 *   Listens for "pdf-highlight-field" and "pdf-clear-highlight" window events
 *   to highlight native AcroForm fields on the PDF preview. Dispatches
 *   "pdf-form-field-hover" when hovering native field overlays on the PDF.
 */
const PdfViewer = {
  mounted() {
    this.currentPage = parseInt(this.el.dataset.page) || 1
    this.pageCount = 0
    this.baseScale = parseFloat(this.el.dataset.scale) || 1.5
    this.scale = this.baseScale
    this.pdfDoc = null
    this.rendering = false
    this.pendingPage = null
    this.fieldOverlays = []
    this._fieldPositions = {} // fieldName → {page, x, y, w, h}
    this._highlightTimer = null
    this._stickyHighlight = false

    try {
      this.fieldOverlays = JSON.parse(this.el.dataset.fieldOverlays || "[]")
    } catch (_) {}

    this._container = this.el.querySelector("[data-pdf-pages]") || this.el
    this._buildUI()

    const url = this.el.dataset.url
    if (url) {
      this._loadPdf(url)
    }

    this.handleEvent("go_to_page", ({ page }) => {
      this._goToPage(page)
    })

    this.handleEvent("set_scale", ({ scale }) => {
      this.baseScale = scale
      this._renderCurrentPage()
    })

    // Re-fit the render scale when the viewport changes
    this._onResize = () => this._renderCurrentPage()
    window.addEventListener("resize", this._onResize)

    // Listen for field highlight requests from form inputs
    this._onHighlightField = (e) => {
      this._stickyHighlight = !!e.detail.sticky
      this._scrollToField(e.detail.name)
    }
    this._onClearHighlight = () => {
      this._stickyHighlight = false
      this._clearHighlight()
    }
    // Listen for field value changes to render live preview on PDF
    this._fieldValues = {} // fieldName → {value, type}
    this._onFieldValueChanged = (e) => {
      const { name, value, type } = e.detail
      this._fieldValues[name] = { value, type }
      this._renderFieldValue(name)
    }

    window.addEventListener("pdf-highlight-field", this._onHighlightField)
    window.addEventListener("pdf-clear-highlight", this._onClearHighlight)
    window.addEventListener("pdf-field-value-changed", this._onFieldValueChanged)
  },

  updated() {
    const newUrl = this.el.dataset.url
    if (newUrl && newUrl !== this._loadedUrl) {
      this._loadPdf(newUrl)
    }
  },

  destroyed() {
    if (this.pdfDoc) {
      this.pdfDoc.destroy()
      this.pdfDoc = null
    }
    window.removeEventListener("pdf-highlight-field", this._onHighlightField)
    window.removeEventListener("pdf-clear-highlight", this._onClearHighlight)
    window.removeEventListener("pdf-field-value-changed", this._onFieldValueChanged)
    window.removeEventListener("resize", this._onResize)
    clearTimeout(this._highlightTimer)
  },

  _buildUI() {
    if (!this.el.querySelector("[data-pdf-canvas]")) {
      this._container.innerHTML = `
        <div class="flex items-center justify-center min-h-48 text-base-content/40">
          <span class="loading loading-spinner loading-md"></span>
        </div>
      `
    }
  },

  async _loadPdf(url) {
    this._loadedUrl = url
    this._fieldPositions = {}
    this._fieldValues = {}
    this._container.innerHTML = `
      <div class="flex items-center justify-center min-h-48 text-base-content/40">
        <span class="loading loading-spinner loading-md"></span>
      </div>
    `

    try {
      const pdfjsLib = await loadPdfJs()
      const loadingTask = pdfjsLib.getDocument(url)
      this.pdfDoc = await loadingTask.promise
      this.pageCount = this.pdfDoc.numPages
      this.currentPage = Math.min(Math.max(this.currentPage, 1), this.pageCount)

      this._container.innerHTML = `
        <div data-pdf-stage style="position:relative;display:inline-block;">
          <canvas data-pdf-canvas style="display:block;"></canvas>
          <div data-field-overlay style="position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;"></div>
          <div data-native-overlay style="position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;"></div>
        </div>
      `
      this.canvas = this._container.querySelector("[data-pdf-canvas]")
      this.ctx = this.canvas.getContext("2d")
      this._overlay = this._container.querySelector("[data-field-overlay]")
      this._nativeOverlay = this._container.querySelector("[data-native-overlay]")

      await this._extractNativeFieldPositions()
      this._addCustomFieldPositions()
      await this._renderCurrentPage()
      this.pushEvent("pdf_loaded", { page_count: this.pageCount })
    } catch (err) {
      console.error("PDF load error:", err)
      this._container.innerHTML = `
        <div class="flex items-center justify-center min-h-48 text-error/60">
          <span>Failed to load PDF</span>
        </div>
      `
      this.pushEvent("pdf_error", { message: err.message || "Failed to load PDF" })
    }
  },

  async _renderCurrentPage() {
    if (!this.pdfDoc || !this.canvas) return

    if (this.rendering) {
      this.pendingPage = this.currentPage
      return
    }

    this.rendering = true

    try {
      const page = await this.pdfDoc.getPage(this.currentPage)

      // Shrink to fit the container width (never upscale past base scale)
      const unscaled = page.getViewport({ scale: 1.0 })
      const styles = getComputedStyle(this.el)
      const padding =
        parseFloat(styles.paddingLeft || 0) + parseFloat(styles.paddingRight || 0)
      const avail = this.el.clientWidth - padding
      this.scale =
        avail > 0 ? Math.min(this.baseScale, avail / unscaled.width) : this.baseScale

      const viewport = page.getViewport({ scale: this.scale })

      const dpr = window.devicePixelRatio || 1
      this.canvas.width = viewport.width * dpr
      this.canvas.height = viewport.height * dpr
      this.canvas.style.width = viewport.width + "px"
      this.canvas.style.height = viewport.height + "px"

      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      await page.render({
        canvasContext: this.ctx,
        viewport: viewport,
      }).promise

      this._renderFieldOverlays()
      this._renderNativeFieldOverlays()
    } catch (err) {
      console.error("PDF render error:", err)
    } finally {
      this.rendering = false
      if (this.pendingPage !== null) {
        this.currentPage = this.pendingPage
        this.pendingPage = null
        this._renderCurrentPage()
      }
    }
  },

  _renderFieldOverlays() {
    if (!this._overlay || this.fieldOverlays.length === 0) return
    this._overlay.innerHTML = ""

    const pageFields = this.fieldOverlays.filter(f => f.page === this.currentPage)
    for (const field of pageFields) {
      const formName = `custom_sig_${field.id}`
      const hasValue = !!this._fieldValues[formName]?.value

      const el = document.createElement("div")
      const sx = field.x * this.scale
      const sy = field.y * this.scale
      const sw = field.w * this.scale
      const sh = field.h * this.scale

      el.setAttribute("data-custom-overlay", formName)
      el.style.cssText = `
        position:absolute;
        left:${sx}px; top:${sy}px;
        width:${sw}px; height:${sh}px;
        border:2px dashed oklch(0.55 0.2 265 / 0.6);
        border-radius:4px;
        background:oklch(0.55 0.2 265 / 0.08);
        display:flex;align-items:center;justify-content:center;
        gap:4px;
        pointer-events:none;
      `

      // Hide label when a value is being previewed
      if (!hasValue) {
        const icon = field.type === "initials" ? "I" : field.type === "signature" ? "✍" : "□"
        const label = field.label || (field.type === "initials" ? "Initials" : "Signature")
        const span = document.createElement("span")
        span.style.cssText = `font-size:${Math.max(9, sh * 0.3)}px;color:oklch(0.45 0.2 265);font-weight:500;`
        span.textContent = `${icon} ${label}`
        el.appendChild(span)
      }

      this._overlay.appendChild(el)
    }
  },

  // Render subtle, hoverable overlays for native AcroForm fields
  _renderNativeFieldOverlays() {
    if (!this._nativeOverlay) return
    this._nativeOverlay.innerHTML = ""

    const pageFields = Object.entries(this._fieldPositions)
      .filter(([_, pos]) => pos.page === this.currentPage)

    if (pageFields.length === 0) return

    for (const [fieldName, pos] of pageFields) {
      const el = document.createElement("div")
      const sx = pos.x * this.scale
      const sy = pos.y * this.scale
      const sw = pos.w * this.scale
      const sh = pos.h * this.scale

      el.setAttribute("data-native-field", fieldName)
      el.style.cssText = `
        position:absolute;
        left:${sx}px; top:${sy}px;
        width:${sw}px; height:${sh}px;
        border:1.5px solid transparent;
        border-radius:3px;
        pointer-events:auto;
        cursor:pointer;
        transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
      `

      // Hover on PDF field → highlight form field on right
      el.addEventListener("mouseenter", () => {
        if (this._stickyHighlight) return
        this._applyHighlightStyle(el)
        window.dispatchEvent(
          new CustomEvent("pdf-form-field-hover", { detail: { name: fieldName, active: true } }),
        )
      })

      el.addEventListener("mouseleave", () => {
        if (this._stickyHighlight) return
        this._removeHighlightStyle(el)
        window.dispatchEvent(
          new CustomEvent("pdf-form-field-hover", { detail: { name: fieldName, active: false } }),
        )
      })

      // Click on PDF field → focus the corresponding form input
      el.addEventListener("click", () => {
        const input = document.querySelector(`[data-pdf-field="${fieldName}"] input, [data-pdf-field="${fieldName}"] select, [data-pdf-field="${fieldName}"] [data-sig-trigger]`)
        if (input) {
          if (input.matches("[data-sig-trigger]")) input.click()
          else input.focus()
        }
      })

      this._nativeOverlay.appendChild(el)

      // Render any existing value
      if (this._fieldValues[fieldName]) {
        this._renderFieldValue(fieldName)
      }
    }
  },

  // Render a live preview of a field value onto its native overlay
  _renderFieldValue(fieldName) {
    // Update the custom field overlay label visibility
    const customOverlay = this._overlay?.querySelector(`[data-custom-overlay="${fieldName}"]`)
    if (customOverlay) {
      const span = customOverlay.querySelector("span")
      const hasValue = !!this._fieldValues[fieldName]?.value
      if (hasValue && span) span.remove()
      if (!hasValue && !customOverlay.querySelector("span")) {
        // Re-render overlays to restore the label
        this._renderFieldOverlays()
      }
    }

    const el = this._nativeOverlay?.querySelector(`[data-native-field="${fieldName}"]`)
    const entry = this._fieldValues[fieldName]
    if (!el) return

    // Remove any existing preview content
    const prev = el.querySelector("[data-value-preview]")
    if (prev) prev.remove()

    if (!entry || !entry.value) return

    const pos = this._fieldPositions[fieldName]
    if (!pos) return
    const sw = pos.w * this.scale
    const sh = pos.h * this.scale

    const preview = document.createElement("div")
    preview.setAttribute("data-value-preview", "")
    preview.style.cssText = `
      position:absolute; inset:0;
      display:flex; align-items:center;
      overflow:hidden; pointer-events:none;
      padding: 0 ${Math.max(2, sw * 0.04)}px;
    `

    if (entry.type === "signature") {
      // Signature: show as image
      const img = document.createElement("img")
      img.src = entry.value
      img.style.cssText = `max-width:100%;max-height:100%;object-fit:contain;`
      preview.appendChild(img)
    } else if (entry.type === "checkbox") {
      // Checkbox: show checkmark
      if (entry.value === "Yes") {
        const check = document.createElement("span")
        check.textContent = "✓"
        check.style.cssText = `font-size:${Math.max(10, sh * 0.7)}px;color:#111827;font-weight:700;line-height:1;`
        preview.style.justifyContent = "center"
        preview.appendChild(check)
      }
    } else {
      // Text: render the value
      const span = document.createElement("span")
      span.textContent = entry.value
      span.style.cssText = `
        font-size:${Math.max(8, Math.min(sh * 0.6, 14))}px;
        color:#111827; font-family:sans-serif;
        white-space:nowrap; overflow:hidden;
        text-overflow:ellipsis; line-height:1;
      `
      preview.appendChild(span)
    }

    el.appendChild(preview)
  },

  async _extractNativeFieldPositions() {
    if (!this.pdfDoc) return
    this._fieldPositions = {}

    const pageNums = Array.from({ length: this.pageCount }, (_, i) => i + 1)
    const results = await Promise.all(
      pageNums.map(async (p) => {
        const page = await this.pdfDoc.getPage(p)
        const viewport = page.getViewport({ scale: 1.0 })
        const annotations = await page.getAnnotations()
        return { p, height: viewport.height, annotations }
      }),
    )

    for (const { p, height, annotations } of results) {
      for (const ann of annotations) {
        if (!ann.fieldName || !ann.rect) continue
        const [x1, y1, x2, y2] = ann.rect
        this._fieldPositions[ann.fieldName] = {
          page: p,
          x: x1,
          y: height - y2,
          w: x2 - x1,
          h: y2 - y1,
        }
      }
    }
  },

  // Add custom-placed fields (signatures, initials) to the position map
  // so they participate in highlight/hover/value-preview
  _addCustomFieldPositions() {
    for (const field of this.fieldOverlays) {
      // Custom fields use "custom_sig_${id}" as form field name
      const formName = `custom_sig_${field.id}`
      this._fieldPositions[formName] = {
        page: field.page,
        x: field.x,
        y: field.y,
        w: field.w,
        h: field.h,
      }
    }
  },

  _scrollToField(fieldName) {
    const pos = this._fieldPositions[fieldName]
    if (!pos) return

    // Navigate to the correct page if needed
    if (pos.page !== this.currentPage) {
      this.currentPage = pos.page
      this._renderCurrentPage().then(() => this._highlightField(fieldName))
    } else {
      this._highlightField(fieldName)
    }
  },

  _highlightField(fieldName) {
    const pos = this._fieldPositions[fieldName]
    if (!pos) return

    // Clear any previous highlight
    this._clearHighlight(true) // keep timer control

    // If there's a native overlay element for this field, style it
    const nativeEl = this._nativeOverlay?.querySelector(`[data-native-field="${fieldName}"]`)
    if (nativeEl) {
      this._applyHighlightStyle(nativeEl)
      nativeEl.setAttribute("data-highlighted", "true")
    }

    // Scroll the container to show the highlighted field
    const sy = pos.y * this.scale
    const scrollContainer = this.el
    const containerRect = scrollContainer.getBoundingClientRect()
    const stageEl = this._container.querySelector("[data-pdf-stage]")
    const stageRect = stageEl ? stageEl.getBoundingClientRect() : { top: 0 }
    const fieldScreenY = stageRect.top - containerRect.top + scrollContainer.scrollTop + sy

    scrollContainer.scrollTo({
      top: Math.max(0, fieldScreenY - containerRect.height / 3),
      behavior: "smooth",
    })

    // If not sticky (hover-only), auto-clear after 2 seconds
    if (!this._stickyHighlight) {
      clearTimeout(this._highlightTimer)
      this._highlightTimer = setTimeout(() => this._clearHighlight(), 2000)
    }
  },

  _applyHighlightStyle(el) {
    el.style.borderColor = "oklch(0.65 0.25 145)"
    el.style.background = "oklch(0.65 0.25 145 / 0.12)"
    el.style.boxShadow = "0 0 8px oklch(0.65 0.25 145 / 0.3)"
  },

  _removeHighlightStyle(el) {
    el.style.borderColor = "transparent"
    el.style.background = "transparent"
    el.style.boxShadow = "none"
  },

  _clearHighlight(skipTimer) {
    if (!skipTimer) clearTimeout(this._highlightTimer)

    if (this._nativeOverlay) {
      for (const el of this._nativeOverlay.querySelectorAll("[data-highlighted]")) {
        this._removeHighlightStyle(el)
        el.removeAttribute("data-highlighted")
      }
    }
  },

  _goToPage(page) {
    const p = parseInt(page, 10)
    if (p >= 1 && p <= this.pageCount) {
      this.currentPage = p
      this._renderCurrentPage()
    }
  },
}

export default PdfViewer
